@if (loading()) {
<app-page-loader loadingType="content" />
} @else {
<app-survey-header [survey]="survey"></app-survey-header>

<div class="p-3 h-[calc(100%-var(--spacing)*15)] overflow-y-auto">
  <app-breadcrumb [items]="[{ label: 'Surveys', route: '/surveys' },{ label: 'Alerts' }]"/>

  @if (error()) {
  <p class="mt-3 rounded-lg border border-red-200 bg-red-100 p-3 text-sm text-red-600">{{ error() }}</p>
  }

  @if (survey) {
  <div class="mt-3 rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
    <div class="border-b border-stroke px-5 py-4 dark:border-strokedark sm:px-7.5">
      <h4 class="text-lg font-semibold text-gray-800 dark:text-gray-300">Alerts</h4>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Configured alert rules for this survey — what triggers them, and how each notification is dispatched.
      </p>
    </div>

    <div class="divide-y divide-gray-100 p-5 dark:divide-gray-800 sm:p-7.5">
      @for (alert of alerts; track alert.id) {
      <div class="py-4 first:pt-0 last:pb-0">
        <button type="button" (click)="toggleAlert(alert.id)" class="flex w-full items-start gap-3 text-left">
          <svg class="mt-1 h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200" [class.-rotate-90]="!isAlertExpanded(alert.id)"
            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="m6 9 6 6 6-6"/>
          </svg>
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <h5 class="text-base font-semibold text-black dark:text-white">{{ alert.title }}</h5>
              <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                {{ getCategoryEntries(alert).length }} template{{ getCategoryEntries(alert).length === 1 ? '' : 's' }}
              </span>
              <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                {{ alert.triggerExpressions.length }} trigger{{ alert.triggerExpressions.length === 1 ? '' : 's' }}
              </span>
            </div>
            @if (alert.description) {
            <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{{ alert.description }}</p>
            }
            <p class="mt-0.5 font-mono text-xs text-gray-400">{{ alert.id }}</p>
          </div>
        </button>

        @if (isAlertExpanded(alert.id)) {
        <div class="mt-4 space-y-5 pl-7">

          <!-- Trigger expressions -->
          <div>
            <h6 class="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200">
              Trigger Expressions
              <app-info-tooltip>
                Raw condition(s) that must be met for this alert to fire.
              </app-info-tooltip>
            </h6>
            <div class="space-y-1.5">
              @for (expr of alert.triggerExpressions; track $index) {
              <p class="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">{{ expr }}</p>
              } @empty {
              <p class="text-sm text-gray-400">No trigger expressions defined.</p>
              }
            </div>
          </div>

          <!-- Dispatch schedule -->
          @if (alert.dispatch) {
          <div>
            <h6 class="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200">
              Dispatch Schedule
              <app-info-tooltip>
                When this alert is sent once triggered — either immediately, or after a configured delay, restricted to the allowed days and UTC time below.
              </app-info-tooltip>
            </h6>
            <div class="grid grid-cols-1 gap-x-6 gap-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700 sm:grid-cols-2">
              <div>
                <span class="text-xs text-gray-500 dark:text-gray-400">Timing</span>
                <p class="text-sm text-gray-800 dark:text-gray-200">
                  @if (alert.dispatch.realTime) {
                  Real-time
                  } @else {
                  {{ alert.dispatch.delayDays }} day{{ alert.dispatch.delayDays === 1 ? '' : 's' }}, {{ alert.dispatch.delayHours }} hour{{ alert.dispatch.delayHours === 1 ? '' : 's' }} delay
                  @if (alert.dispatch.delayFromNow) {
                  <span class="text-gray-400">(from now)</span>
                  }
                  }
                </p>
              </div>
              <div>
                <span class="text-xs text-gray-500 dark:text-gray-400">Time (UTC)</span>
                <p class="text-sm text-gray-800 dark:text-gray-200">{{ alert.dispatch.timeUtc }}</p>
              </div>
              <div class="sm:col-span-2">
                <span class="text-xs text-gray-500 dark:text-gray-400">Allowed Days</span>
                <div class="mt-1 flex flex-wrap gap-1">
                  @for (day of parseWeekDays(alert.dispatch.weekDays); track day) {
                  <span class="rounded-full border border-brand-200 bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-700 dark:border-brand-500/30 dark:bg-brand-500/10 dark:text-brand-400">{{ day }}</span>
                  } @empty {
                  <span class="text-sm text-gray-400">Not defined.</span>
                  }
                </div>
              </div>
            </div>
          </div>
          }

          <!-- Categories / dispatch templates -->
          <div>
            <h6 class="mb-1.5 text-sm font-semibold text-gray-700 dark:text-gray-200">Dispatch Templates</h6>
            <div class="space-y-3">
              @for (category of getCategoryEntries(alert); track category.key) {
              <div class="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                <div class="border-b border-gray-200 bg-gray-50 px-4 py-2.5 dark:border-gray-700 dark:bg-gray-800">
                  <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">{{ category.key }}</span>
                </div>

                <div class="space-y-4 p-4">
                  <!-- Notification details -->
                  <div class="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
                    @if (hasValue(category.value.notification.provider)) {
                    <div><span class="text-xs text-gray-500 dark:text-gray-400">Provider</span><p class="text-sm text-gray-800 dark:text-gray-200">{{ category.value.notification.provider }}</p></div>
                    }
                    @if (hasValue(category.value.notification.categoryType)) {
                    <div><span class="text-xs text-gray-500 dark:text-gray-400">Category Type</span><p class="text-sm text-gray-800 dark:text-gray-200">{{ category.value.notification.categoryType }}</p></div>
                    }
                    @if (hasValue(category.value.notification.renderMode)) {
                    <div><span class="text-xs text-gray-500 dark:text-gray-400">Render Mode</span><p class="text-sm text-gray-800 dark:text-gray-200">{{ category.value.notification.renderMode }}</p></div>
                    }
                    @if (hasValue(category.value.notification.source)) {
                    <div><span class="text-xs text-gray-500 dark:text-gray-400">Source</span><p class="text-sm text-gray-800 dark:text-gray-200">{{ category.value.notification.source }}</p></div>
                    }
                    @if (hasValue(category.value.notification.subject)) {
                    <div class="sm:col-span-2"><span class="text-xs text-gray-500 dark:text-gray-400">Subject</span><p class="text-sm text-gray-800 dark:text-gray-200">{{ category.value.notification.subject }}</p></div>
                    }
                    @if (hasValue(category.value.notification.senderName) || hasValue(category.value.notification.senderAddress)) {
                    <div>
                      <span class="text-xs text-gray-500 dark:text-gray-400">Sender</span>
                      <p class="text-sm text-gray-800 dark:text-gray-200">
                        {{ category.value.notification.senderName }}
                        @if (hasValue(category.value.notification.senderAddress)) {
                        <span class="text-gray-400">&lt;{{ category.value.notification.senderAddress }}&gt;</span>
                        }
                      </p>
                    </div>
                    }
                    @if (hasValue(category.value.notification.receivers)) {
                    <div><span class="text-xs text-gray-500 dark:text-gray-400">Receivers</span><p class="text-sm text-gray-800 dark:text-gray-200">{{ category.value.notification.receivers }}</p></div>
                    }
                    @if (hasValue(category.value.notification.dispatchUTCFrom) || hasValue(category.value.notification.dispatchUTCTo)) {
                    <!--div><span class="text-xs text-gray-500 dark:text-gray-400">Dispatch Window (UTC)</span><p class="text-sm text-gray-800 dark:text-gray-200">{{ category.value.notification.dispatchUTCFrom }} - {{ category.value.notification.dispatchUTCTo }}</p></div-->
                    }
                    @if (hasValue(category.value.notification.weekDays)) {
                    <div><span class="text-xs text-gray-500 dark:text-gray-400">Week Days</span><p class="text-sm text-gray-800 dark:text-gray-200">{{ category.value.notification.weekDays }}</p></div>
                    }
                    @if (hasValue(category.value.notification.externalRefID)) {
                    <div><span class="text-xs text-gray-500 dark:text-gray-400">External Ref ID</span><p class="font-mono text-xs text-gray-600 dark:text-gray-400">{{ category.value.notification.externalRefID }}</p></div>
                    }
                    @if (hasValue(category.value.notification.batchId)) {
                    <div><span class="text-xs text-gray-500 dark:text-gray-400">Batch ID</span><p class="font-mono text-xs text-gray-600 dark:text-gray-400">{{ category.value.notification.batchId }}</p></div>
                    }
                  </div>

                  <!-- Message content -->
                  @if (hasValue(category.value.notification.plainText)) {
                  <div>
                    <span class="mb-1 block text-xs text-gray-500 dark:text-gray-400">Plain Text</span>
                    <pre class="max-h-40 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs whitespace-pre-wrap text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">{{ category.value.notification.plainText }}</pre>
                  </div>
                  }
                  @if (hasValue(category.value.notification.htmlText)) {
                  <div>
                    <span class="mb-1 block text-xs text-gray-500 dark:text-gray-400">HTML</span>
                    <pre class="max-h-40 overflow-auto rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs whitespace-pre-wrap text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">{{ category.value.notification.htmlText }}</pre>
                  </div>
                  }

                  <!-- Distributions -->
                  @if (category.value.distributions.length > 0) {
                  @let distId = 'dist-' + alert.id + '-' + category.key;
                  <div>
                    <button type="button" (click)="toggleSection(distId)"
                      class="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                      <svg class="h-3.5 w-3.5 shrink-0 transition-transform duration-200" [class.-rotate-90]="!isSectionExpanded(distId)"
                        viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="m6 9 6 6 6-6"/>
                      </svg>
                      Distributions
                      <span class="rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-normal text-gray-500 dark:bg-gray-800 dark:text-gray-400">{{ category.value.distributions.length }}</span>
                    </button>
                    @if (isSectionExpanded(distId)) {
                    <div class="mt-1.5 flex flex-wrap gap-1.5">
                      @for (dist of category.value.distributions; track dist.id) {
                      <span class="rounded-full border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
                        @if (hasValue(dist.cmdPropField)) { {{ dist.cmdPropField }}: {{ dist.cmdPropValue }} } @else { {{ dist.id }} }
                      </span>
                      }
                    </div>
                    }
                  </div>
                  }

                  <!-- Triggers -->
                  @if (getTriggerEntries(category.value).length > 0) {
                  <div>
                    <span class="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">Triggers</span>
                    <div class="space-y-2">
                      @for (trigger of getTriggerEntries(category.value); track trigger.key) {
                      <div class="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700">
                        <p class="font-mono text-xs text-gray-500 dark:text-gray-400"><b>Question Code: </b><span class="font-semibold text-gray-700 dark:text-gray-300">{{ trigger.value.expressionCode }}</span></p>
                        @if (trigger.value.expressionDescription) {
                        <p class="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400"><b>Description: </b>{{ trigger.value.expressionDescription }}</p>
                        }
                        <p class="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400"><b>Expression: </b>{{ trigger.value.expression }}</p>
                        @if (trigger.value.dl.length > 0) {
                        @let dlId = 'dl-' + alert.id + '-' + category.key + '-' + trigger.key;
                        <button type="button" (click)="toggleSection(dlId)"
                          class="mt-1 flex items-center gap-1.5 font-mono text-xs text-gray-500 dark:text-gray-400">
                          <svg class="h-3 w-3 shrink-0 transition-transform duration-200" [class.-rotate-90]="!isSectionExpanded(dlId)"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="m6 9 6 6 6-6"/>
                          </svg>
                          <b>Distribution List</b> ({{ trigger.value.dl.length }})
                        </button>
                        @if (isSectionExpanded(dlId)) {
                        <div class="mt-1.5 flex flex-wrap gap-1">
                          @for (dl of trigger.value.dl; track dl) {
                          <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">{{ dl }}</span>
                          }
                        </div>
                        }
                        }
                      </div>
                      }
                    </div>
                  </div>
                  }

                  <!-- Email template -->
                  @if (hasValue(category.value.emailTemplate.urlTemplate) || hasValue(category.value.emailTemplate.urlTemplateRTL) || getOptionalTagEntries(category.value).length > 0) {
                  <div>
                    <span class="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">Email Template</span>
                    <div class="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700">
                      <div class="flex flex-wrap gap-2">
                        @if (hasValue(category.value.emailTemplate.urlTemplate)) {
                        <a [href]="category.value.emailTemplate.urlTemplate" target="_blank" rel="noopener"
                          class="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-brand-600 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800">
                          <svg class="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                          Preview Template
                        </a>
                        }
                        @if (hasValue(category.value.emailTemplate.urlTemplateRTL)) {
                        <a [href]="category.value.emailTemplate.urlTemplateRTL" target="_blank" rel="noopener"
                          class="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-brand-600 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800">
                          <svg class="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                          Preview Template (RTL)
                        </a>
                        }
                      </div>

                      @if (getOptionalTagEntries(category.value).length > 0) {
                      <div class="mt-2 space-y-1.5">
                        <span class="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">Optional Tags</span>
                        @for (tag of getOptionalTagEntries(category.value); track tag.key) {
                        <div class="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-700">
                          <div class="flex flex-wrap items-center gap-2">
                            <span class="font-mono text-xs font-semibold text-gray-700 dark:text-gray-300">{{ tag.key }}</span>
                            <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-300">{{ tag.value.type }}</span>
                          </div>
                          @if (tag.value.segments.length > 0) {
                          <div class="mt-1.5 flex flex-wrap gap-1">
                            @for (segment of tag.value.segments; track segment) {
                            <span class="rounded-full border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">{{ segment }}</span>
                            }
                          </div>
                          }
                        </div>
                        }
                      </div>
                      }
                    </div>
                  </div>
                  }
                </div>
              </div>
              } @empty {
              <p class="text-sm text-gray-400">No dispatch templates configured.</p>
              }
            </div>
          </div>

        </div>
        }
      </div>
      } @empty {
      <p class="py-6 text-center text-sm text-gray-400">No alerts configured for this survey.</p>
      }
    </div>
  </div>
  }
</div>
}
